import React from "react";
import { AccentComboUnbound } from "../../controls/unbound/AccentComboUnbound";
import { queryNoTracking } from "../../services/DataService";

const query_WhatsAppConfigurations = queryNoTracking("WhatsAppIntegration_GetConfigurations");

export default function ChatWhatsAppNumberSelector({
    value = null,           // selected phone number config ID (Guid)
    onChange = null,        // (phoneNumberConfig) => void
    currentUserBusinessUnitId = null,  // for smart defaulting
    disabled = false,
}) {
    const [loading, setLoading] = React.useState(true);
    const [phoneNumbers, setPhoneNumbers] = React.useState([]);
    const [selectedId, setSelectedId] = React.useState(value);

    // Load configurations once
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const result = await query_WhatsAppConfigurations.getAll();
                if (!cancelled) {
                    // Extract phone number settings - handle array response
                    const settings = Array.isArray(result)
                        ? result[0]?.PhoneNumberSettings || []
                        : result?.PhoneNumberSettings || [];

                    // Transform for display
                    const phoneNumberItems = settings.map(s => ({
                        ID: s.ID,
                        Description: `${s.DisplayName} — ${s.PhoneNumber}`,
                        PhoneNumber: s.PhoneNumber,
                        SenderNumberID: s.SenderNumberID,
                        IsFallback: s.IsFallback,
                        BusinessUnitIDs: s.BusinessUnitIDs,
                        // Include full config for onChange callback
                        _fullConfig: s
                    }));

                    setPhoneNumbers(phoneNumberItems);

                    // Smart defaulting logic - only if no value was provided
                    if (!value && phoneNumberItems.length > 0) {
                        let defaultConfig = null;

                        // Try to find config matching user's BU
                        if (currentUserBusinessUnitId) {
                            defaultConfig = phoneNumberItems.find(s =>
                                s.BusinessUnitIDs?.includes(currentUserBusinessUnitId)
                            );
                        }

                        // Fall back to fallback config
                        if (!defaultConfig) {
                            defaultConfig = phoneNumberItems.find(s => s.IsFallback);
                        }

                        // Last resort: first config
                        if (!defaultConfig) {
                            defaultConfig = phoneNumberItems[0];
                        }

                        if (defaultConfig) {
                            setSelectedId(defaultConfig.ID);
                            // Trigger onChange with the full config
                            onChange?.(defaultConfig._fullConfig);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to load WhatsApp configurations", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [value, currentUserBusinessUnitId, onChange]);

    return (
        <AccentComboUnbound
            key={`whatsapp-number-${selectedId}-${loading}`}  // Force remount when selection changes
            label="Send From *"
            displayField="Description"
            valueField="ID"
            required
            defaultValue={selectedId}
            onChange={(newSelectedId) => {
                setSelectedId(newSelectedId);
                const selected = phoneNumbers.find(p => p.ID === newSelectedId);
                onChange?.(selected?._fullConfig || null);
            }}
            data={phoneNumbers}
            placeholder={loading ? "Loading configurations..." : "Select a phone number..."}
            disabled={disabled || loading}
        />
    );
}